// AI session context — exposes the user's provider config and a
// `sendMessage` helper to the panes. Loads the config from
// IndexedDB on mount and keeps it in React state so the AI pane
// can react to provider changes without a full page reload.
//
// Privacy boundary (Req 22.5): every call inside this module talks
// directly to the provider URL the user picked. The Link backend
// is never on the path.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { streamCompletion, type AiTurn } from "./aiClient";
import {
  clearAiProvider,
  loadAiProvider,
  saveAiProvider,
  type AiProviderConfig,
} from "./aiProvider";

interface AiSessionValue {
  /** `null` until the IndexedDB read settles. */
  config: AiProviderConfig | null;
  /** True once the initial load has completed (success or empty). */
  ready: boolean;
  /** Persist a new config and update the in-memory state. */
  save: (next: AiProviderConfig) => Promise<void>;
  /** Forget the saved config (also wipes the IndexedDB row). */
  clear: () => Promise<void>;
  /**
   * Stream a completion. Emits chunks via `onChunk`; resolves when
   * the stream closes. Throws on transport / provider errors.
   */
  send: (
    turns: ReadonlyArray<AiTurn>,
    onChunk: (chunk: string) => void,
  ) => Promise<void>;
}

const AiSessionContext = createContext<AiSessionValue | null>(null);

export function AiSessionProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AiProviderConfig | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadAiProvider()
      .then((value) => {
        if (cancelled) return;
        setConfig(value);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = useCallback(async (next: AiProviderConfig) => {
    await saveAiProvider(next);
    setConfig(next);
  }, []);

  const clear = useCallback(async () => {
    await clearAiProvider();
    setConfig(null);
  }, []);

  const send = useCallback(
    async (
      turns: ReadonlyArray<AiTurn>,
      onChunk: (chunk: string) => void,
    ) => {
      if (config === null) {
        throw new Error("No AI provider configured.");
      }
      await streamCompletion(config, turns, onChunk);
    },
    [config],
  );

  const value = useMemo<AiSessionValue>(
    () => ({ config, ready, save, clear, send }),
    [config, ready, save, clear, send],
  );

  return (
    <AiSessionContext.Provider value={value}>
      {children}
    </AiSessionContext.Provider>
  );
}

export function useAiSession(): AiSessionValue {
  const ctx = useContext(AiSessionContext);
  if (ctx === null) {
    throw new Error("useAiSession must be used inside <AiSessionProvider>");
  }
  return ctx;
}
